import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatMoney } from '../../utils/formatMoney';
import { Trash2, X, Search, Plus, Minus, RefreshCw, Loader2, CheckCircle, ArrowLeft, Home } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import api, { productsAPI } from '../../services/api';
import { useToast } from './Toast';
import ProductCatalog, { CatalogView } from './ProductCatalog';
import type { Product, Category } from '../../types/order';
import { productDisplayPrice } from '../../utils/pricing';
import { wasteEstimateCents, wasteEstimateTotal } from '../../utils/adminPriceDisplay';
import { fmtTime } from '../home/format';

/**
 * Καταχώρηση φύρας από το POS (μόνο όταν το κατάστημα έχει waste_enabled).
 * Ίδιο catalog UX με το ταμείο (οθόνες αφής): κατηγορίες → προϊόντα, tap για
 * προσθήκη — όχι πληκτρολόγηση. Οι τιμές παγώνουν server-side σε τιμές
 * πώλησης. Δεύτερη καρτέλα δείχνει τις καταχωρήσεις της τρέχουσας περιόδου
 * ταμείου (διαγραφή: δικές του μέχρι το κλείσιμο — μετά, μόνο ο διαχειριστής).
 */

export const WASTE_REASON_LABELS: Record<string, string> = {
  expired: 'Ληγμένο',
  damaged: 'Κατεστραμμένο',
  misprep: 'Λάθος παρασκευή',
  other: 'Άλλο',
};

interface PendingLine {
  product: Product;
  quantity: number;
  reason: string;
}

interface WasteEntry {
  id: number;
  employee_id: number | null;
  employee_name: string | null;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_value: number;
  reason: string | null;
  created_at: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const WasteModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { store, employee } = useAuth();
  const { showToast } = useToast();

  const [tab, setTab] = useState<'add' | 'period'>('add');
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);

  // Catalog browsing state (ίδιο μοντέλο με TakeAway)
  const [currentView, setCurrentView] = useState<CatalogView>('categories');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [pending, setPending] = useState<PendingLine[]>([]);
  const [entries, setEntries] = useState<WasteEntry[]>([]);
  const [summary, setSummary] = useState<{ qty: number; value: number }>({ qty: 0, value: 0 });
  const [saving, setSaving] = useState(false);

  const euro = (n: number) => `${formatMoney((Number(n) || 0))}`;

  const loadEntries = useCallback(async () => {
    if (!store?.id) return;
    try {
      const res = await api.get(`/waste/${store.id}`);
      setEntries(res.data.entries || []);
      setSummary({ qty: res.data.summary?.qty || 0, value: res.data.summary?.value || 0 });
    } catch { /* best-effort */ }
  }, [store?.id]);

  useEffect(() => {
    if (!isOpen || !store?.id) return;
    setPending([]);
    setTab('add');
    setCurrentView('categories');
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSearchTerm('');
    setLoading(true);
    Promise.all([
      productsAPI.getCategories(store.id)
        .then((data: any) => setCategories((data || []).filter((c: Category) => c.is_active)))
        .catch(() => setCategories([])),
      productsAPI.getProducts(store.id)
        .then((data: any) => {
          const list = (data.products || data || []) as Product[];
          // Τα προϊόντα-τέλη (σακούλες κλπ) δεν είναι φύρα.
          setProducts(list.filter((p: any) => !p.fee_category));
        })
        .catch(() => setProducts([])),
      loadEntries(),
    ]).finally(() => setLoading(false));
  }, [isOpen, store?.id, loadEntries]);

  const getSelectedCategoryObj = () => categories.find(c => c.id === selectedCategory);

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category.id);
    setSearchTerm('');
    if (category.subcategories && category.subcategories.length > 0) {
      setCurrentView('subcategories');
    } else {
      setSelectedSubcategory(null);
      setCurrentView('products');
    }
  };

  const handleSubcategorySelect = (subcategory: Category | null) => {
    setSelectedSubcategory(subcategory?.id || null);
    setSearchTerm('');
    setCurrentView('products');
  };

  const handleBack = () => {
    if (currentView === 'products') {
      const category = getSelectedCategoryObj();
      if (category?.subcategories && category.subcategories.length > 0) {
        setCurrentView('subcategories');
      } else {
        setSelectedCategory(null);
        setCurrentView('categories');
      }
    } else if (currentView === 'subcategories') {
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setCurrentView('categories');
    }
  };

  const handleHome = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSearchTerm('');
    setCurrentView('categories');
  };

  const addProduct = (p: Product) => {
    setPending(prev => {
      const idx = prev.findIndex(l => l.product.id === p.id);
      if (idx >= 0) return prev.map((l, i) => i === idx ? { ...l, quantity: l.quantity + 1 } : l);
      return [...prev, { product: p, quantity: 1, reason: 'expired' }];
    });
  };

  const setQty = (idx: number, qty: number) => {
    if (qty <= 0) {
      setPending(prev => prev.filter((_, i) => i !== idx));
    } else {
      setPending(prev => prev.map((l, i) => i === idx ? { ...l, quantity: Math.min(qty, 999) } : l));
    }
  };

  // ΕΚΤΙΜΗΣΗ με την τιμή take-away του πλακιδίου. Την αξία που αποθηκεύεται τη βγάζει ο
  // server (utils/sellingPrice.js).
  const pendingTotal = useMemo(() => wasteEstimateTotal(pending), [pending]);

  const save = async () => {
    if (!store?.id || pending.length === 0) return;
    setSaving(true);
    try {
      await api.post(`/waste/${store.id}`, {
        entries: pending.map(l => ({ product_id: l.product.id, quantity: l.quantity, reason: l.reason })),
      });
      showToast('success', 'Η φύρα καταχωρήθηκε.');
      setPending([]);
      await loadEntries();
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Αποτυχία καταχώρησης φύρας.');
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (id: number) => {
    if (!store?.id) return;
    try {
      await api.delete(`/waste/${store.id}/${id}`);
      showToast('success', 'Η καταχώρηση διαγράφηκε.');
      await loadEntries();
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Αποτυχία διαγραφής.');
    }
  };

  if (!isOpen) return null;

  const isAdmin = ['store_admin', 'super_admin', 'admin', 'manager'].includes(employee?.role || '');

  return (
    <div
      className="cashier-overlay fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div className="cashier-dialog bg-surface-1 rounded-2xl w-full max-w-4xl h-[94vh] overflow-hidden shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cashier-dialog-header flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-line shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-600/20 shrink-0">
              <Trash2 className="w-5 h-5 text-amber-700 dark:text-amber-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-t1 truncate">Φύρα</h2>
              <p className="text-xs text-t3 truncate">Αξία σε τιμές πώλησης — δεν επηρεάζει το ταμείο</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Tabs */}
            <div className="flex bg-surface-2 rounded-xl p-1">
              <button
                onClick={() => setTab('add')}
                className={`px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'add' ? 'bg-amber-600 text-slate-950' : 'text-t2'}`}
              >
                Καταχώρηση
              </button>
              <button
                onClick={() => setTab('period')}
                className={`px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'period' ? 'bg-amber-600 text-slate-950' : 'text-t2'}`}
              >
                Περίοδος ({summary.qty})
              </button>
            </div>
            <button onClick={onClose} className="w-10 h-10 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center active:scale-95 transition-all">
              <X className="w-5 h-5 text-t2" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><RefreshCw className="w-8 h-8 text-amber-700 dark:text-amber-400 animate-spin" /></div>
        ) : tab === 'add' ? (
          <>
            {/* Catalog toolbar: back/home + search */}
            <div className="flex items-center gap-2 px-4 sm:px-5 py-2.5 border-b border-line/60 shrink-0">
              {currentView !== 'categories' && (
                <>
                  <button onClick={handleBack} className="w-10 h-10 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center active:scale-95 shrink-0">
                    <ArrowLeft className="w-5 h-5 text-t2" />
                  </button>
                  <button onClick={handleHome} className="w-10 h-10 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center active:scale-95 shrink-0">
                    <Home className="w-5 h-5 text-t2" />
                  </button>
                </>
              )}
              <span className="text-sm font-semibold text-t2 truncate flex-1">
                {searchTerm.trim()
                  ? 'Αναζήτηση'
                  : currentView === 'categories'
                    ? 'Κατηγορίες — πάτησε προϊόν για προσθήκη'
                    : getSelectedCategoryObj()?.name || ''}
              </span>
              <div className="relative w-44 sm:w-56 shrink-0">
                <Search className="w-4 h-4 text-t4 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Αναζήτηση…"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-line-strong rounded-xl bg-surface-2 text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                />
              </div>
            </div>

            {/* Catalog grid (ίδιο component με το ταμείο) */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4">
              <ProductCatalog
                categories={categories}
                products={products}
                loading={false}
                currentView={currentView}
                selectedCategory={selectedCategory}
                selectedSubcategory={selectedSubcategory}
                searchTerm={searchTerm}
                categoryColumns={3}
                productColumns={3}
                onCategorySelect={handleCategorySelect}
                onSubcategorySelect={handleSubcategorySelect}
                onProductClick={addProduct}
                onProductMouseDown={() => {}}
                onProductMouseUp={() => {}}
                getProductPrice={(p) => productDisplayPrice(p, 'take_away')}
              />
            </div>

            {/* Pending lines + save (sticky footer) */}
            {pending.length > 0 && (
              <div className="border-t border-line bg-surface-1 shrink-0 p-3 sm:p-4 space-y-2 max-h-[40vh] overflow-y-auto">
                {pending.map((l, idx) => (
                  <div key={l.product.id} className="bg-surface-2 dark:bg-surface-2/50 border border-line-strong/50 rounded-xl p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-t1 truncate">{l.product.name}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button onClick={() => setQty(idx, l.quantity - 1)} className="w-10 h-10 rounded-lg bg-surface-3 hover:bg-surface-4 flex items-center justify-center active:scale-95">
                          <Minus className="w-4 h-4 text-t2" />
                        </button>
                        <span className="w-10 text-center text-base font-bold text-t1">{l.quantity}</span>
                        <button onClick={() => setQty(idx, l.quantity + 1)} className="w-10 h-10 rounded-lg bg-surface-3 hover:bg-surface-4 flex items-center justify-center active:scale-95">
                          <Plus className="w-4 h-4 text-t2" />
                        </button>
                        <button onClick={() => setQty(idx, 0)} className="w-10 h-10 rounded-lg bg-surface-3 dark:bg-surface-3/60 hover:bg-red-600/30 flex items-center justify-center active:scale-95 ml-1">
                          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(WASTE_REASON_LABELS).map(([key, label]) => (
                          <button
                            key={key}
                            onClick={() => setPending(prev => prev.map((x, i) => i === idx ? { ...x, reason: key } : x))}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${l.reason === key ? 'bg-amber-600 text-slate-950' : 'bg-surface-3 dark:bg-surface-3/60 text-t2 hover:bg-surface-3'}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <span className="text-xs text-t3 shrink-0">{euro(wasteEstimateCents(l.product, l.quantity) / 100)}</span>
                    </div>
                  </div>
                ))}
                <button
                  onClick={save}
                  disabled={saving}
                  className="w-full py-3.5 rounded-xl bg-amber-600 hover:bg-amber-700 disabled:bg-surface-3 text-slate-950 disabled:text-t3 font-bold text-base flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                  Καταχώρηση φύρας — {pending.reduce((s, l) => s + l.quantity, 0)} τεμ ({euro(pendingTotal)})
                </button>
              </div>
            )}
          </>
        ) : (
          /* Περίοδος tab — καταχωρήσεις τρέχουσας περιόδου ταμείου */
          <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-t3 uppercase tracking-wider">Τρέχουσα περίοδος ταμείου</p>
              <p className="text-sm text-t2">{summary.qty} τεμ · <span className="text-amber-700 dark:text-amber-400 font-semibold">{euro(summary.value)}</span></p>
            </div>
            {entries.length === 0 ? (
              <p className="text-sm text-t4 text-center py-10">Καμία καταχώρηση φύρας στην περίοδο</p>
            ) : (
              <div className="space-y-1.5">
                {entries.map(e => (
                  <div key={e.id} className="flex items-center justify-between gap-2 bg-surface-2 dark:bg-surface-2/40 border border-line-strong/40 rounded-lg px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm text-t2 truncate">
                        {e.product_name} <span className="text-t3">× {e.quantity}</span>
                      </p>
                      <p className="text-[11px] text-t4">
                        {/* 24ωρη ώρα Ελλάδας, όπως η «Φύρα» της διαχείρισης (ως 14/10 «10:15 μ.μ.» — το el-GR είναι 12ωρο) */}
                        {fmtTime(e.created_at)}
                        {e.reason ? ` · ${WASTE_REASON_LABELS[e.reason] || e.reason}` : ''}
                        {e.employee_name ? ` · ${e.employee_name}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm text-t2">{euro(e.total_value)}</span>
                      {(isAdmin || e.employee_id === employee?.id) && (
                        <button
                          onClick={() => deleteEntry(e.id)}
                          className="w-9 h-9 rounded-lg bg-surface-3 dark:bg-surface-3/60 hover:bg-red-600/30 flex items-center justify-center active:scale-95"
                          title="Διαγραφή"
                        >
                          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WasteModal;
